import { useCallback, useEffect, useRef, useState } from 'react';
import { useGetIdentity, useNotify } from 'react-admin';
import { MoneyRequest, MoneyRequestPage } from '../../services/moneyRequestApi';

/** The signed-in admin's id (from /auth/me), used to hide Approve on one's own requests. */
export function useCurrentUserId(): string | undefined {
  const { data } = useGetIdentity();
  return (data as any)?.id ? String((data as any).id) : undefined;
}

/**
 * Loads one request list and offers `run`, which executes an action, reports its error, and reloads.
 * The server is the authority: hiding a button is a convenience, never the control.
 */
export function useRequestList(
  fetcher: (params: { page: number; size: number; status?: string }) => Promise<MoneyRequestPage>,
  status?: string
) {
  const notify = useNotify();
  const [rows, setRows] = useState<MoneyRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetcherRef.current({ page: 1, size: 50, status: status || undefined });
      setRows(res.data ?? []);
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [status, notify]);

  useEffect(() => {
    load();
  }, [load]);

  /** Run an action on one row; returns its result, or undefined if it threw (already notified). */
  const run = useCallback(
    async <T>(id: string, action: () => Promise<T>, okMessage?: string) => {
      setBusyId(id);
      try {
        const res = await action();
        if (okMessage) notify(okMessage, { type: 'success' });
        return res;
      } catch (e: any) {
        notify(e?.message || 'Error', { type: 'error' });
        return undefined;
      } finally {
        setBusyId(null);
        load();
      }
    },
    [load, notify]
  );

  return { rows, loading, busyId, load, run, setBusyId };
}
